import { useState, useRef, useContext, useEffect } from "react"; // Added useRef to handle file input reference
import {
  Button,
  Form,
  Row,
  Col,
  DatePicker,
  Spin,
  Space,
  Select,
  Statistic,
} from "antd";
import FormLayout, {
  FormContent,
  FormHeader,
} from "../../../components/form/form";
import {
  UploadOutlined,
  DownloadOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import AuthUtil from "../../../utils/auth";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import {
  GetCustomerList,
  GetTicketBulkDataReport,
  GetTicketUploadSample,
  ImportTicketList,
} from "../../../utils/api.constant";
import {
  eHTTPStatusCode,
  eResultCode,
  eSERVICEREQUEST,
} from "../../../utils/enum";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import { UserContext } from "../../../context";
import {
  requestType,
  STAKE_HOLDERS,
  UNAUTHORIZEDREQUEST,
} from "../../../utils/constants";
import dayjs from "dayjs";
import CustomModal from "../../../components/modal";
import DateUtils from "../../../utils/date";

type DrawerProps = {
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

type TBulkUploadResp = {
  dealerNotFound: number;
  dealerFound: number;
  ticketFailureCount: number;
  ticketSuccessCount: number;
  tempTicketBulkUploadId: number;
};

/**
 * Component for handling bulk upload functionality.
 * Allows users to upload XLSX files and process the data.
 */
export default function BulkUploadTicketForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { onShowToast } = useToast();
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref to handle file input
  const [selectedName, setSelectedName] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [bulkUploadResponse, setBulkUploadResponse] =
    useState<TBulkUploadResp>();
  const [selectedRequests, setSelectedRequests] = useState<TDropdownOptions[]>(
    []
  );
  const context: any = useContext(UserContext);

  useEffect(() => {
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldValue("customerId", context?.user?.orgId);
      setSelectedName(context?.user?.orgName);
    }
  }, [context?.user]);

  /**
   * Handles file selection from the input.
   * Validates the file type and updates the state accordingly.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the file input
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("customer id", form.getFieldValue("customerId"));
    console.log("selected request", selectedRequests);
    if (form.getFieldValue("customerId") && selectedRequests.length) {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        const isXlsx =
          selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        if (!isXlsx) {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: "Please upload an XLSX file!",
          });
          setFile(null); // Reset file state
          event.target.value = ""; // Clear the input
        } else {
          setFile(selectedFile); // Set the selected file
          handleSubmit(selectedFile);
        }
      }

      // Reset the input value to ensure `onChange` is triggered for the same file
      event.target.value = "";
    } else {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please fill all the mandatory fields",
      });
      setFile(null); // Reset file state
      event.target.value = ""; // Clear the input
      return;
    }
  };

  /**
   * Handles the submission of the bulk upload form.
   * Validates the file and if valid, sends a POST request to the server to process the file.
   * If the response is successful, refreshes the list and closes the drawer.
   * If the response is not successful, shows an error toast with the error message.
   */
  const handleSubmit = async (file: any) => {
    // if (
    //   !form.getFieldValue("customerId") ||
    //   // !form.getFieldValue("bulkUploadDate") &&
    //   !form.getFieldValue("ticketType") ||
    //   !selectedRequests
    // ) {
    //   onShowToast({
    //     type: ToastType.error,
    //     title: <CloseOutlined />,
    //     position: ToastOpen.centerTop,
    //     content: "Please fill all the mandatory fields",
    //   });
    //   return;
    // }
    if (!file) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please select an XLSX file!",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    // const bulkUploadDate = form.getFieldValue("bulkUploadDate");
    const jsonData = {
      ticketType: eSERVICEREQUEST.OTHER,
      customerId: form.getFieldValue("customerId"),
      // bulkUploadDate: bulkUploadDate
      //   ? DateUtils.getDateStringWithOutTimezone(
      //       new Date(bulkUploadDate).toDateString()
      //     )
      //   : null,
      serviceRequestType: selectedRequests,
    };
    formData.append("file", file);
    formData.append("data", JSON.stringify(jsonData));

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${ImportTicketList}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;

        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          const {
            tempTicketBulkUploadId,
            dealerFoundCount,
            dealerNotFoundCount,
            ticketFailureCount,
            ticketSuccessCount,
            ...restData
          } = responseData.data;
          setBulkUploadResponse({
            tempTicketBulkUploadId,
            ticketFailureCount,
            ticketSuccessCount,
            dealerFound: dealerFoundCount,
            dealerNotFound: dealerNotFoundCount,
          });
          onShowToast({
            type: ToastType.success,
            title: <CheckOutlined />,
            position: ToastOpen.centerTop,
            content: responseData.dataResponse.description,
          });
          setIsModalOpen(true);
          props?.onRefreshList();
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: responseData.dataResponse.description,
          });
        }
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessedFileDownload = async () => {
    if (!bulkUploadResponse?.tempTicketBulkUploadId) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Required field is missing",
      });
      return;
    }
    try {
      // Start loading
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetTicketBulkDataReport}`,
        {
          method: "POST", // Change to POST
          headers: {
            "Content-Type": "application/json", // Set content type for JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              tempTicketBulkUploadId:
                bulkUploadResponse?.tempTicketBulkUploadId,
            },
          }),
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = `${selectedName}_Bulk_Ticket_Status.xlsx`; // Set the desired file name with .xlsx extension

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Error while downloading file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  // Function to download bulk upload sample template
  const handleDownload = async () => {
    if (!form.getFieldValue("customerId")) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please select customer to complete this operation",
      });
      return;
    }
    try {
      // Start loading
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetTicketUploadSample}`,
        {
          method: "POST", // Change to POST
          headers: {
            "Content-Type": "application/json", // Set content type for JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              // bulkUploadDate: form.getFieldValue("bulkUploadDate"), // Move SampleId to body
              customerId: form.getFieldValue("customerId"), // Move customerId to body
            },
          }),
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = `${selectedName}_Bulk_Ticket_Upload_Sample.xlsx`; // Set the desired file name with .xlsx extension

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content:
            "Error while downloading sample file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  // Function to trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handles the cancel button click on the modal, and closes the modal.
   */
  const handleCancel = () => {
    setIsModalOpen(false);
    setFile(null);
  };
  const contentStyle: React.CSSProperties = {
    padding: 50,
  };

  const content = <div style={contentStyle} />;
  return (
    <>
      <CustomModal
        title={"Bulk Ticket Status"}
        useCustomHeader={true}
        isOpen={isModalOpen}
        // isFullScreen={false}
        onOk={() => {}} // Passing the custom function
        onCancel={handleCancel}
        hideOkButton={true}
      >
        <div className="">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1rem",
              // padding: "0 1rem",
            }}
          >
            <div style={{ display: "flex", gap: "1rem" }}>
              {/* </Form.Item> */}
              <div>
                <Button
                  disabled={isLoading}
                  type="primary"
                  style={{ marginRight: "10px" }}
                  onClick={handleProcessedFileDownload}
                >
                  <DownloadOutlined style={{ fontSize: "22px" }} />
                  Bulk Tickets Report
                </Button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "2rem" }}>
            <Row gutter={16} style={{ marginBottom: "20px" }}>
              <Col span={12}>
                <Statistic
                  title="Dealer Found"
                  value={bulkUploadResponse?.dealerFound}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Dealer Not Found"
                  value={bulkUploadResponse?.dealerNotFound}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Success Ticket"
                  value={bulkUploadResponse?.ticketSuccessCount}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Failure Ticket"
                  value={bulkUploadResponse?.ticketFailureCount}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: "10px" }}>
              <Col span={24}>
                {bulkUploadResponse?.ticketFailureCount ? (
                  <span style={{ color: "red" }}>
                    {`Note: We found errors in ${bulkUploadResponse?.ticketFailureCount} records while creating the ticket. To review the details, please download the updated file by clicking the 'Bulk Tickets Report' button. After correcting the errors, re-upload the file to proceed.`}
                  </span>
                ) : (
                  <span style={{ color: "var(--bs-primary-dark)" }}>
                    {`The file has been successfully validated without any errors and ${bulkUploadResponse?.ticketSuccessCount} tickets are created successfully.`}
                  </span>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </CustomModal>
      <FormLayout>
        <Form
          autoComplete="off"
          onFinish={handleSubmit}
          layout="vertical"
          name="bulkUploadForm"
          form={form}
        >
          <FormHeader>
            <CloseOutlined
              style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
              onClick={() => {
                form.resetFields();
                setFile(null);
                props.onCloseDrawer();
              }}
            />
          </FormHeader>
          <FormContent>
            <Row gutter={16} className="">
              <Space>
                <Col span={24}>
                  <h2 style={{ color: "#fff", marginTop: "10px" }}>
                    Import Tickets
                  </h2>
                </Col>
              </Space>
            </Row>
            <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="customerId"
                  label="Customer"
                  rules={[
                    { required: true, message: "Please choose customer" },
                  ]}
                >
                  <CustomDropdown
                    isDisabled={
                      context?.user?.stakeHolderName ===
                        STAKE_HOLDERS.CUSTOMER || isLoading
                        ? true
                        : false
                    }
                    endPoint={GetCustomerList}
                    value={form.getFieldValue("customerId")}
                    selectedName={selectedName}
                    onChange={(selected: any) => {
                      if (selected?.servicePartners?.length) {
                        form.setFieldValue("customerId", selected.value);
                        setSelectedName(selected.label);
                      } else {
                        form.setFieldValue("customerId", null);
                        setSelectedName("");
                        onShowToast({
                          type: ToastType.error,
                          title: <CloseOutlined />,
                          position: ToastOpen.centerTop,
                          content:
                            "Service Partner is not mapped with this customer",
                        });
                      }
                    }}
                    label=""
                    placeHolder="Select customer"
                  ></CustomDropdown>
                </Form.Item>
              </Col>
            </Row>
            {/* <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="bulkUploadDate"
                  label="Service Date"
                  rules={[
                    {
                      required: true,
                      message: "Please select service date",
                    },
                  ]}
                >
                  <DatePicker
                    minDate={dayjs(new Date())}
                    format="DD-MMM-YYYY"
                    placeholder="Select service date"
                  />
                </Form.Item>
              </Col>
            </Row> */}
            <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="serviceRequestType"
                  label="Service Request Type"
                  rules={[
                    { required: true, message: "Please select request type" },
                  ]}
                >
                  <Select
                    mode="multiple"
                    maxTagCount={1} // Limits the displayed tag to 1
                    placeholder="Please select request type"
                    onChange={(newValue, option: any) => {
                      if (newValue.length > 1) {
                        // Keep only the most recently selected option
                        const latestSelection = [newValue[newValue.length - 1]];
                        setSelectedRequests([option[option.length - 1]]);

                        // Update the Form.Item value
                        form.setFieldValue(
                          "serviceRequestType",
                          latestSelection
                        );
                      } else {
                        setSelectedRequests(
                          (option as any[])?.map(
                            (item: TDropdownOptions) => item
                          )
                        );
                      }
                    }}
                    filterOption={(input, option: any) => {
                      return option?.label
                        ? option.label
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        : false;
                    }}
                    options={requestType}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} className="formRowHeight">
              {isLoading ? (
                <Col
                  span={24}
                  style={{
                    justifyContent: "center",
                    display: "flex",
                    marginTop: "2rem",
                    flexDirection: "column",
                  }}
                >
                  <Spin
                    tip={
                      <span
                        style={{
                          color: "#fff",
                          fontFamily: "Montserrat-Regular",
                        }}
                      >
                        Processing . . .
                      </span>
                    }
                    size="large"
                  >
                    {content}
                  </Spin>
                </Col>
              ) : (
                <Col
                  span={24}
                  style={{
                    justifyContent: "flex-start",
                    display: "flex",
                    marginTop: "2rem",
                    flexDirection: "column",
                  }}
                >
                  <Form.Item
                    label=""
                    style={{ marginBottom: 0 }}
                    // dependencies={["customerId", "uploadMonth"]}
                  >
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      style={{ display: "none" }} // Hide the input
                    />
                    <Button
                      disabled={isLoading}
                      onClick={triggerFileInput}
                      icon={<UploadOutlined />}
                      style={{
                        width: "100%",
                        textOverflow: "ellipsis", // Truncate text
                        overflow: "hidden", // Hide overflow content
                        whiteSpace: "nowrap", // Prevent wrapping to a new line
                      }}
                    >
                      {file ? (
                        <span
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </span>
                      ) : (
                        "Choose to Upload"
                      )}
                    </Button>
                  </Form.Item>
                  <Button
                    type="default"
                    disabled={isLoading}
                    style={{ marginTop: "1rem" }}
                    onClick={handleDownload}
                    icon={<DownloadOutlined />}
                  >
                    Template
                  </Button>
                </Col>
              )}
            </Row>
          </FormContent>
        </Form>
      </FormLayout>
    </>
  );
}
